import { createSlice } from '@reduxjs/toolkit';
import { resolveSecurityDeposit } from '../../utils/helpers';
import { DEFAULT_TENURE } from '../../constants/app';

const loadCartFromStorage = () => {
  try {
    const stored = localStorage.getItem('hbfurniture_cart');
    return stored ? JSON.parse(stored) : { items: [], totalQuantity: 0, totalAmount: 0, totalDeposit: 0 };
  } catch {
    return { items: [], totalQuantity: 0, totalAmount: 0, totalDeposit: 0 };
  }
};

const saveCartToStorage = (state) => {
  try {
    localStorage.setItem('hbfurniture_cart', JSON.stringify(state));
  } catch (error) {
    console.error('Failed to save cart:', error);
  }
};

const recalculateTotals = (state) => {
  state.totalQuantity = state.items.reduce((sum, item) => sum + item.quantity, 0);
  state.totalAmount = state.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
  state.totalDeposit = state.items.reduce((sum, item) => {
    const deposit = resolveSecurityDeposit(item, item.price);
    return sum + (deposit * item.quantity);
  }, 0);
};

const cartSlice = createSlice({
  name: 'cart',
  initialState: loadCartFromStorage(),
  reducers: {
    addToCart(state, action) {
      const newItem = {
        ...action.payload,
        basePrice: Number(action.payload?.basePrice || action.payload?.price || 0),
        tenure: Number(action.payload?.tenure || DEFAULT_TENURE),
      };
      const existingItem = state.items.find(item => 
        item.id === newItem.id && 
        item.tenure === newItem.tenure
      );
      
      if (existingItem) {
        existingItem.quantity += 1;
      } else {
        state.items.push({
          ...newItem,
          quantity: 1,
        });
      }
      
      recalculateTotals(state);
      saveCartToStorage(state);
    },
    
    removeFromCart(state, action) {
      const { id, tenure } = action.payload;
      state.items = state.items.filter(item => !(item.id === id && item.tenure === tenure));
      recalculateTotals(state);
      saveCartToStorage(state);
    },
    
    updateQuantity(state, action) {
      const { id, tenure, quantity } = action.payload;
      const item = state.items.find(item => item.id === id && item.tenure === tenure);
      
      if (item && quantity > 0) {
        item.quantity = quantity;
        recalculateTotals(state);
        saveCartToStorage(state);
      }
    },
    
    updateTenure(state, action) {
      const { id, oldTenure, newTenure, newPrice } = action.payload;
      const oldIndex = state.items.findIndex((item) => item.id === id && item.tenure === oldTenure);
      if (oldIndex < 0) return;

      const existingIndex = state.items.findIndex(
        (item, index) => index !== oldIndex && item.id === id && item.tenure === newTenure
      );

      if (existingIndex >= 0) {
        state.items[existingIndex].quantity += state.items[oldIndex].quantity;
        state.items[existingIndex].price = newPrice;
        state.items.splice(oldIndex, 1);
      } else {
        state.items[oldIndex].tenure = newTenure;
        state.items[oldIndex].price = newPrice;
      }

      recalculateTotals(state);
      saveCartToStorage(state);
    },
    
    updateAllTenures(state, action) {
      const { newTenure, prices } = action.payload;
      state.items = state.items.map((item, i) => ({
        ...item,
        tenure: newTenure,
        price: prices[i],
      }));
      recalculateTotals(state);
      saveCartToStorage(state);
    },

    clearCart(state) {
      state.items = [];
      state.totalQuantity = 0;
      state.totalAmount = 0;
      state.totalDeposit = 0;
      saveCartToStorage(state);
    },
  },
});

export const { addToCart, removeFromCart, updateQuantity, updateTenure, updateAllTenures, clearCart } = cartSlice.actions;
export default cartSlice.reducer;
